// @greenlight-spec: 19-design-system.md

/**
 * Minimal ANSI SGR (Select Graphic Rendition) parser for the runtime log
 * viewer. Pod stdout/stderr can embed ANSI color escapes; we decode the common
 * foreground / background / bold codes into semantic spans so the log viewer can
 * render color (docs/19 § Log viewer) without taking an ANSI dependency.
 *
 * Only SGR sequences (`ESC[…m`) are interpreted; any other escape sequence
 * (cursor moves, line clears) is stripped rather than rendered literally.
 */

export type AnsiColor = 'red' | 'green' | 'yellow' | 'blue' | 'magenta' | 'cyan' | 'white' | 'gray';

export interface AnsiSpan {
  readonly text: string;
  readonly fg?: AnsiColor;
  readonly bg?: AnsiColor;
  readonly bold?: boolean;
}

interface AnsiStyle {
  fg?: AnsiColor;
  bg?: AnsiColor;
  bold?: boolean;
}

// Foreground codes — 30–37 standard, 90–97 bright. Black (30/90) maps to gray
// so it stays legible on the dark log surface rather than vanishing.
const FG: Readonly<Record<number, AnsiColor>> = {
  30: 'gray',
  31: 'red',
  32: 'green',
  33: 'yellow',
  34: 'blue',
  35: 'magenta',
  36: 'cyan',
  37: 'white',
  90: 'gray',
  91: 'red',
  92: 'green',
  93: 'yellow',
  94: 'blue',
  95: 'magenta',
  96: 'cyan',
  97: 'white',
};

// Background codes — 40–47 standard, 100–107 bright.
const BG: Readonly<Record<number, AnsiColor>> = {
  40: 'gray',
  41: 'red',
  42: 'green',
  43: 'yellow',
  44: 'blue',
  45: 'magenta',
  46: 'cyan',
  47: 'white',
  100: 'gray',
  101: 'red',
  102: 'green',
  103: 'yellow',
  104: 'blue',
  105: 'magenta',
  106: 'cyan',
  107: 'white',
};

// The ESC byte (0x1B). Built at runtime so the source carries no control char
// and the regexes below stay clear of the no-control-regex lint.
const ESC = String.fromCharCode(27);
// Any CSI escape sequence: ESC [ <params> <intermediates> <final byte>.
const ANSI_ESCAPE = new RegExp(`${ESC}\\[[0-9;?]*[ -/]*[@-~]`, 'g');
// SGR specifically (terminated by `m`), capturing its parameter list.
const SGR = new RegExp(`${ESC}\\[([0-9;]*)m`, 'g');

function applyCodes(style: AnsiStyle, params: string): AnsiStyle {
  // An empty parameter list (`ESC[m`) is equivalent to a reset.
  const codes = params === '' ? [0] : params.split(';').map((p) => Number(p) || 0);
  const next: AnsiStyle = { ...style };
  for (const code of codes) {
    if (code === 0) {
      next.fg = undefined;
      next.bg = undefined;
      next.bold = undefined;
    } else if (code === 1) {
      next.bold = true;
    } else if (code === 22) {
      next.bold = undefined;
    } else if (code === 39) {
      next.fg = undefined;
    } else if (code === 49) {
      next.bg = undefined;
    } else if (code in FG) {
      next.fg = FG[code];
    } else if (code in BG) {
      next.bg = BG[code];
    }
    // Other SGR codes (italic, underline, 256/truecolor) are ignored at MVP.
  }
  return next;
}

function toSpan(text: string, style: AnsiStyle): AnsiSpan {
  return {
    text,
    ...(style.fg ? { fg: style.fg } : {}),
    ...(style.bg ? { bg: style.bg } : {}),
    ...(style.bold ? { bold: true } : {}),
  };
}

/**
 * Splits a log line into styled segments. The running style carries across
 * segments until a reset or override; non-SGR escapes are stripped from the
 * visible text. Always returns at least one span so callers can map freely.
 */
export function parseAnsi(line: string): AnsiSpan[] {
  const spans: AnsiSpan[] = [];
  let style: AnsiStyle = {};
  let lastIndex = 0;
  SGR.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SGR.exec(line)) !== null) {
    const chunk = line.slice(lastIndex, match.index).replace(ANSI_ESCAPE, '');
    if (chunk) spans.push(toSpan(chunk, style));
    style = applyCodes(style, match[1] ?? '');
    lastIndex = SGR.lastIndex;
  }
  const tail = line.slice(lastIndex).replace(ANSI_ESCAPE, '');
  if (tail) spans.push(toSpan(tail, style));
  if (spans.length === 0) spans.push({ text: '' });
  return spans;
}

/** Strips every ANSI escape sequence, leaving plain text (for clipboard/filter). */
export function stripAnsi(line: string): string {
  return line.replace(ANSI_ESCAPE, '');
}
